/*
 * THE PROJECTION: world to screen and back, through a tilt and a spin.
 *
 * Ported from `reference/universe-v8/engine.js` — `updateView` (579–586), `toWorld` (588–594),
 * `toScreen` (595–600) and `worldDelta` (601–605). Checked against those by running them.
 *
 * OUR RENDERER HAS NEVER HAD THIS. `core/map.ts`'s `toScreen` is a translate and a scale; theirs
 * also rotates the world about the camera and foreshortens it vertically, and the amount of both
 * depends on how far out the reader is. That is the zoomed-out view where the field reads as a disc
 * seen at an angle rather than as a flat diagram — and it is why their dust appears to have
 * parallax, which the first port noted as missing and could not explain.
 *
 * THREE NUMBERS DESCRIBE THE VIEW, and everything else is derived from them:
 *
 *   `vc`, `vs`   cosine and sine of the spin — the world turning slowly under the reader
 *   `vt`         the tilt: 1 is flat on, falling to 0.6 fully tilted, squashing y toward the horizon
 *
 * and `pcx`/`pcy` are the camera's own position already put through them, so a point costs one
 * rotate and one scale rather than a matrix.
 *
 * `squash` (their `SQ`) is a separate, constant vertical scale on the WORLD — the layout is laid
 * out in a squashed space — where `vt` is the animated tilt of the VIEW. Both divide y and they are
 * not the same thing; conflating them is the obvious mistake here.
 *
 * Pure functions over an explicit view, holding no state, so they can be checked against the
 * reference exactly and used from a worker.
 */

/** Where the camera looks and how close. */
export interface Camera {
  x: number;
  y: number;
  z: number;
}

/** The viewport, in CSS pixels. */
export interface Viewport {
  width: number;
  height: number;
}

/** What the projection needs beyond the camera: the world's squash, the spin and the tilt. */
export interface ViewInput {
  cam: Camera;
  view: Viewport;
  /** The world's own vertical scale. 1 unless the layout was laid out squashed. */
  squash: number;
  /** How far the spin has turned, in radians. */
  spinAngle: number;
  /** How much of the tilt is in force, 0 (flat on) to 1 (fully tilted). */
  spinFraction: number;
  /** The reader's own preference for how much tilt there is at all. 1 by default. */
  tiltAmount?: number;
}

/** The projection, resolved once and then used many times a frame. */
export interface View {
  /** Cosine and sine of the spin. */
  vc: number;
  vs: number;
  /** The tilt: 1 flat on, less as the reader pulls back. */
  vt: number;
  /** The camera's position, already projected — so a point costs one rotate and one scale. */
  pcx: number;
  pcy: number;
  cam: Camera;
  view: Viewport;
  squash: number;
}

/**
 * HOW MUCH THE TILT CAN TAKE, at its fullest.
 *
 * Their 0.40: fully pulled back, the world is squashed to 60% of its height. Held as a constant
 * rather than inlined because it is the one number that says how three-dimensional this reads.
 */
const TILT_DEPTH = 0.40;

/** Resolve the view. Their `updateView`. */
export function resolveView({ cam, view, squash, spinAngle, spinFraction, tiltAmount }: ViewInput): View {
  const sq = squash || 1;
  const vc = Math.cos(spinAngle);
  const vs = Math.sin(spinAngle);
  const vt = 1 - TILT_DEPTH * spinFraction * (tiltAmount ?? 1);
  const u = cam.x;
  const v = cam.y / sq;
  return {
    vc,
    vs,
    vt,
    pcx: u * vc - v * vs,
    pcy: (u * vs + v * vc) * sq * vt,
    cam,
    view,
    squash: sq,
  };
}

/** Where a world point lands on screen. Their `toScreen`. */
export function toScreen(v: View, x: number, y: number): { x: number; y: number } {
  const sq = v.squash || 1;
  const z = v.cam.z;
  const u = x;
  const w = y / sq;
  return {
    x: (u * v.vc - w * v.vs - v.pcx) * z + v.view.width / 2,
    y: ((u * v.vs + w * v.vc) * sq * v.vt - v.pcy) * z + v.view.height / 2,
  };
}

/**
 * THE PROJECTION AS A CANVAS MATRIX — six numbers that do what `toScreen` does, per pixel, for free.
 *
 * Their comment at line 1451 says the whole thing: *"the projection is affine, so the cached field
 * can ride it exactly, tilt and spin included"*. Expand `toScreen` and every term is either constant
 * or linear in `x` and `y`, which means it IS `[a b c d e f]` and the canvas can apply it in
 * hardware to an arbitrary image.
 *
 * THAT IS WHY A CACHED LAYER IS POSSIBLE AT ALL. A texture drawn once in world coordinates can be
 * blitted under this matrix at any camera and land exactly where drawing it afresh would have put
 * it — spin, tilt, squash, zoom and pan included. Without it a cache would only be valid for the
 * camera it was built at, which is no cache. `wash.ts` is the layer that lives on this, and it is
 * the difference between one `drawImage` and eighty nested gradient discs a frame.
 *
 * `dpr` scales the whole matrix, because the backing store is in device pixels while everything
 * above is in CSS pixels.
 */
export function projectionMatrix(v: View, dpr: number): [number, number, number, number, number, number] {
  const sq = v.squash || 1;
  const z = v.cam.z;
  return [
    z * v.vc * dpr,
    z * sq * v.vt * v.vs * dpr,
    (-z * v.vs / sq) * dpr,
    z * v.vt * v.vc * dpr,
    (-z * v.pcx + v.view.width / 2) * dpr,
    (-z * v.pcy + v.view.height / 2) * dpr,
  ];
}

/** And back: what world point is under a screen point. Their `toWorld`. */
export function toWorld(v: View, sx: number, sy: number): { x: number; y: number } {
  const sq = v.squash || 1;
  const z = v.cam.z;
  const X = (sx - v.view.width / 2) / z + v.pcx;
  const Y = (sy - v.view.height / 2) / z + v.pcy;
  const V = Y / (sq * v.vt);
  return { x: X * v.vc + V * v.vs, y: (-X * v.vs + V * v.vc) * sq };
}

/**
 * HOW FAR OUT A READER MAY PULL, and why it moves.
 *
 * Their `minZoom` (290–298). Three answers:
 *
 * - Held close during the arrival (0.72), because the flight is about one name at a time and the
 *   whole field is not the reader's to see yet.
 * - Otherwise, far enough out that the whole world fits — and **never further**, because the node
 *   alpha ramps in from 0.035 and a floor below that is a blank screen. Their comment says so, and
 *   the first port recorded the same bug found the hard way (`UNIVERSE-MAP.md`, "two bugs the
 *   design records worth not repeating").
 * - On the map it OPENS AS THE READER WALKS: 0.30, pulling back by the square root of how many
 *   names they have opened. The field they may survey grows with what they have earned, and the
 *   square root is what keeps the hundredth name from opening it as much as the first.
 *
 * `rim` is REQUIRED and has no default. Their 12,320 is where their 10,000 mock names happened to
 * settle; ours is whatever our own layout settles to, and a default would quietly be their world.
 *
 * AND SO ARE THE FOUR ZOOMS, which is a correction. This function took the rim and then went on
 * using their 0.72, 0.036, 0.30 and 0.030 as though a zoom were portable. It is not — a zoom is
 * pixels per world unit, and their 0.30 frames about two fifths of a world 12,320 across. Read in a
 * world a quarter the size it frames the whole thing and then some, which is a map that opens on an
 * empty screen with the names in a clot at the middle. Found by wiring the engine to our roster and
 * looking at it; not findable from the differential, because the differential passes their numbers.
 */
export interface FloorZooms {
  /**
   * How close the reader is held during the arrival — the flight is about one name at a time.
   */
  held: number;
  /**
   * The furthest out the floor may ever go.
   *
   * Never below the zoom the node alpha ramps in from, or the map is a blank screen. Their comment
   * says so and the first port found the same bug the hard way.
   */
  faintest: number;
  /** Where the map's own floor starts, and how much each name walked opens it by. */
  mapBase: number;
  perVisited: number;
}

/**
 * THE ZOOM THAT FITS THE WHOLE FIELD — theirs, and the base every flight is a multiple of.
 *
 * Pulled out of `minZoom` because the floor stopped being this number. While the map floor was
 * measured against the crowd the two were the same, and `flyTarget` multiplied tour beats by the
 * floor with no difference; with the floor back at theirs they are seven times apart, and the
 * arrival's opening beat — a multiple of 1, meaning "the whole field, look how much there is" —
 * was showing a seventh of it. Two names for one quantity, until they were not.
 */
export function wholeField(view: Viewport, rim: number, faintest: number): number {
  return Math.max(faintest, Math.min(view.width || 1200, view.height || 800) / (rim * 2.06));
}

export function minZoom(phase: string, view: Viewport, visited: number, rim: number, floors: FloorZooms): number {
  if (phase === 'ground' || phase === 'named') return floors.held;
  const whole = wholeField(view, rim, floors.faintest);
  if (phase !== 'map') return whole;
  /* The square root is what keeps the hundredth name from opening the field as much as the first. */
  return Math.max(whole, floors.mapBase - Math.sqrt(visited) * floors.perVisited);
}

/**
 * How far the world moved, for a drag of so many screen pixels. Their `worldDelta`.
 *
 * Not `toWorld(a) - toWorld(b)`: the camera offset cancels in a difference, so this drops it and
 * keeps a drag exact however far from the origin the reader has wandered.
 */
export function worldDelta(v: View, dsx: number, dsy: number): { x: number; y: number } {
  const sq = v.squash || 1;
  const z = v.cam.z;
  const dX = dsx / z;
  const dV = dsy / z / (sq * v.vt);
  return { x: dX * v.vc + dV * v.vs, y: (-dX * v.vs + dV * v.vc) * sq };
}
